/* eslint-disable */
// src/services/auth.ts
//
// Native (in-SPA) Cognito authentication built on aws-amplify v6 (modular
// imports only). The app signs users in directly from the branded /login page
// (SRP — the password is never transmitted), Amplify caches and REFRESHES the
// tokens transparently, and no app flow ever navigates to the hosted UI. The
// hosted-UI authorization-code flow remains configured as a fallback
// (Amplify's own oauth listener with internal PKCE), but nothing links to it.
//
// Everything environment-specific comes from the runtime config
// (/config/config.json via getGlobalConfig) — nothing is hardcoded.

import { Amplify } from "aws-amplify";
import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { getGlobalConfig } from "../config/config.js"; // Your config getter
import store from "../store/store";

/**
 * Storage keys used by PREVIOUS versions of this app (manual token bundle +
 * static Apollo bearer token). Nothing reads them anymore; stale copies from
 * old sessions are exactly what caused login redirect loops, so they are
 * removed unconditionally at startup and on sign-out.
 */
const LEGACY_STORAGE_KEYS = ["cognito_tokens", "luxweb"];

export function purgeLegacyAuthArtifacts() {
  try {
    LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    // Storage unavailable — nothing to purge.
  }
}

/**
 * Configure Amplify Auth from the runtime config. Called once at startup
 * (main.ts -> initializeApp) AFTER loadConfig().
 *
 * Self-healing by construction: Amplify caches tokens under keys scoped to
 * the CONFIGURED app client id, so tokens from another environment/pool are
 * simply never picked up.
 */
export const initializeAuth = () => {
  const config = getGlobalConfig();

  if (!config?.aws_user_pools_id || !config?.aws_user_pools_web_client_id) {
    throw new Error("Cognito configuration is missing!");
  }

  purgeLegacyAuthArtifacts();

  const redirectSignIn = (config.redirect_sign_in || "")
    .split(",")
    .filter(Boolean);
  const redirectSignOut = (config.redirect_sign_out || "")
    .split(",")
    .filter(Boolean);
  const scopes = (config.supported_logins || "openid email profile")
    .split(/[ ,]+/)
    .filter(Boolean);

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.aws_user_pools_id,
        userPoolClientId: config.aws_user_pools_web_client_id,
        loginWith: {
          email: true,
          // Hosted-UI fallback only — the app's own flows never navigate
          // there. Amplify handles the code exchange + PKCE internally.
          ...(config.oauth_domain
            ? {
                oauth: {
                  domain: config.oauth_domain,
                  scopes,
                  redirectSignIn,
                  redirectSignOut,
                  responseType: "code" as const,
                },
              }
            : {}),
        },
      },
    },
  });
};

/** Identity payload for the store's setLoggedIn action (from the ID token). */
function identityFromSession(session: any): any | null {
  const payload = session?.tokens?.idToken?.payload;
  if (!payload) {
    return null;
  }
  return {
    sub: payload.sub,
    username: payload["cognito:username"] || payload.email || payload.sub,
    email: payload.email,
  };
}

/** Mark the store logged-in from the current Amplify session. */
async function markLoggedInFromSession(): Promise<void> {
  const session = await fetchAuthSession();
  const identity = identityFromSession(session);
  if (!identity) {
    throw new Error("Sign-in did not produce a session.");
  }
  store.dispatch("setLoggedIn", identity);
}

/**
 * Await-able sign-out used before starting a NEW auth flow (e.g. the /welcome
 * invite flow must override any existing session — Amplify's signIn throws
 * UserAlreadyAuthenticatedException otherwise).
 */
export async function ensureSignedOut(): Promise<void> {
  try {
    await signOut();
  } catch (e) {
    /* best effort */
  }
  store.dispatch("setLoggedOut");
  purgeLegacyAuthArtifacts();
}

export interface NativeLoginResult {
  /** True when Cognito requires a new password before the login completes. */
  newPasswordRequired: boolean;
}

/**
 * Ask the backend to adopt this credential from the SE1 pool.
 *
 * Users invited into V2 but never signed in hold a temporary password they have never seen,
 * while their working password is still in SE1. The backend verifies the credential against
 * SE1 and, only if SE1 accepts it, writes it into V2 — so the retry below succeeds and the
 * user is never shown a password-reset screen.
 *
 * Returns false on any failure, including a non-2xx or a missing endpoint, so an environment
 * without SE1 configured behaves exactly as before.
 */
async function tryAdoptSE1Password(
  username: string,
  password: string
): Promise<boolean> {
  try {
    const res = await fetch("/graphql/adopt-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: username, password }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { adopted?: boolean };
    return body?.adopted === true;
  } catch {
    return false;
  }
}

/**
 * Sign in with username/password (Amplify signIn — SRP by default, so the
 * password never leaves the browser). Returns newPasswordRequired=true when
 * Cognito answers the NEW_PASSWORD_REQUIRED challenge (invited users signing
 * in with a temporary password) — complete it with completeNewPassword().
 *
 * `captchaToken` is DORMANT plumbing (the retired Google reCAPTCHA rollback
 * path): when provided it rides to Cognito via ClientMetadata. Sign-in abuse
 * protection is AWS WAF on the user pool — see services/waf-captcha.ts.
 */
export async function authenticate(
  username: string,
  password: string,
  captchaToken?: string
): Promise<NativeLoginResult> {
  try {
    const { isSignedIn, nextStep } = await signIn({
      username,
      password,
      ...(captchaToken
        ? { options: { clientMetadata: { captchaToken } } }
        : {}),
    });
    if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      return { newPasswordRequired: true };
    }
    if (!isSignedIn) {
      throw new Error(
        `Sign-in requires an unsupported step (${nextStep?.signInStep}). Please contact support.`
      );
    }
    await markLoggedInFromSession();
    return { newPasswordRequired: false };
  } catch (error: any) {
    if (error?.name === "UserAlreadyAuthenticatedException") {
      // A session already exists — treat as signed in.
      await markLoggedInFromSession();
      return { newPasswordRequired: false };
    }
    // A rejected credential may simply be one that only exists in SE1 yet. Ask the backend to
    // verify it there and adopt it; if it does, the same credential now works, so retry once.
    // Only retried on an actual credential rejection, and only once, so a genuinely wrong
    // password still fails fast with the original error.
    if (error?.name === "NotAuthorizedException") {
      const adopted = await tryAdoptSE1Password(username, password);
      if (adopted) {
        return authenticate(username, password, captchaToken);
      }
    }
    throw error;
  }
}

/** Complete the NEW_PASSWORD_REQUIRED challenge started by authenticate(). */
export async function completeNewPassword(newPassword: string): Promise<void> {
  const { isSignedIn, nextStep } = await confirmSignIn({
    challengeResponse: newPassword,
  });
  if (!isSignedIn) {
    throw new Error(
      `Password change requires an unsupported step (${nextStep?.signInStep}).`
    );
  }
  await markLoggedInFromSession();
}

/**
 * Ensures a fresh session (Amplify refreshes expired tokens transparently)
 * and marks the user logged in from the ID token identity. Throws when no
 * usable session exists — route guards then redirect to /login.
 */
async function getAuthenticatedUser(): Promise<string> {
  const session = await fetchAuthSession();
  const identity = identityFromSession(session);
  if (!session.tokens?.accessToken || !identity) {
    store.dispatch("setLoggedOut");
    throw new Error("User session is not available.");
  }
  if (!store.getters.loggedIn) {
    store.dispatch("setLoggedIn", identity);
  }
  return identity.username || "";
}

/**
 * Current bearer token for API calls (ACCESS token — what the backend's JWKS
 * verification has always been sent). Amplify refreshes it when expired.
 * Returns null when signed out.
 */
export async function getCurrentAccessToken(
  forceRefresh = false
): Promise<string | null> {
  try {
    const session = await fetchAuthSession(
      forceRefresh ? { forceRefresh: true } : undefined
    );
    return session.tokens?.accessToken?.toString() ?? null;
  } catch (e) {
    return null;
  }
}

/** True when Amplify has a cached session for the CONFIGURED app client. */
export function hasStoredSession(): boolean {
  const config = getGlobalConfig();
  try {
    return !!localStorage.getItem(
      `CognitoIdentityServiceProvider.${config?.aws_user_pools_web_client_id}.LastAuthUser`
    );
  } catch (e) {
    return false;
  }
}

/**
 * Hosted-UI sign-in redirect — FALLBACK ONLY (kept functional, unused by the
 * app's own flows). Amplify performs the authorization request with its own
 * internally-generated PKCE parameters.
 */
async function login(_loginHint?: string) {
  await signInWithRedirect();
}

/**
 * Hosted-UI callback (fallback flow): Amplify processes the ?code= in the URL
 * itself; we just wait for the session to materialize.
 */
export async function parseCognitoWebResponse(_url: string): Promise<void> {
  for (let attempt = 0; attempt < 10; attempt++) {
    const session = await fetchAuthSession().catch(() => null);
    if (session?.tokens?.accessToken) {
      await markLoggedInFromSession();
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
  throw new Error("Hosted-UI sign-in did not produce a session.");
}

/**
 * Signs the user out entirely inside the SPA: Amplify clears its cached
 * tokens locally, the store is reset, legacy artifacts are purged — no
 * external redirect (the router then forwards to /login).
 */
/** Belt-and-suspenders: expire every cookie on sign-out. Tokens live in
 *  localStorage (Amplify default), but this guarantees NO stale auth/session
 *  cookie survives a logout — a fully clean slate as the operator requested. */
function clearAllCookies() {
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (!name) return;
      const expire = "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = `${name}${expire};path=/`;
      document.cookie = `${name}${expire}`;
    });
  } catch (e) {
    /* cookies unavailable — nothing to clear */
  }
}

function logout() {
  store.dispatch("setLoggedOut");
  purgeLegacyAuthArtifacts();
  clearAllCookies();
  // global:true also REVOKES the refresh token SERVER-SIDE, so a copied/cached
  // token can't be reused after sign-out. Fire-and-forget: also clears Amplify's
  // local token cache.
  signOut({ global: true }).catch((error) => {
    console.warn("Sign-out cleanup failed:", error);
  });
}

/**
 * Ask the backend to email a one-time link to /reset.
 *
 * NOT Amplify's resetPassword. Cognito's own ForgotPassword code is fixed at
 * one hour with no setting to change it, and it silently sends NOTHING for a
 * user in FORCE_CHANGE_PASSWORD — every admin-created account that has never
 * signed in, which is most of the people who need a reset. The backend issues
 * its own single-use ticket instead (see helpers/password-reset).
 *
 * Always resolves. The endpoint answers identically for a registered and an
 * unknown address, so this page can never be used to discover who has an
 * account; the caller always shows "check your email".
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await fetch("/graphql/request-password-reset", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export interface PasswordResetResult {
  reset: boolean;
  /** Why it failed, safe to show: a policy complaint or "link expired". */
  message?: string;
}

/**
 * Redeem the ticket from the emailed link and set the new password.
 *
 * The token identifies the account by itself, so nothing else is needed and
 * nothing about the user is guessed from the URL. On success the caller sends
 * them to /login rather than signing them in — an automatic sign-in would mean
 * a second WAF CAPTCHA in the middle of the flow.
 */
export async function redeemPasswordReset(
  token: string,
  password: string
): Promise<PasswordResetResult> {
  try {
    const res = await fetch("/graphql/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    const body = (await res.json()) as PasswordResetResult;
    return { reset: body?.reset === true, message: body?.message };
  } catch (e) {
    return { reset: false, message: "Could not reach the server. Please try again." };
  }
}

// Export the functions as part of the default service API
export default {
  login,
  logout,
  authenticate,
  completeNewPassword,
  requestPasswordReset,
  redeemPasswordReset,
  ensureSignedOut,
  getAuthenticatedUser,
  getCurrentAccessToken,
  parseCognitoWebResponse,
  hasStoredSession,
};
